/**
 * Intelligence Batch Activity Feed
 * ---------------------------------------------------------------
 * Reads `content_events` for batch lifecycle events
 * (started / completed / failed) and renders a tactical activity
 * log showing timestamp, asset count, archetypes, formats,
 * generation duration, and status.
 *
 * Refreshes on the global CONTENT_ASSETS_CHANGED_EVENT and on a
 * subtle interval so the panel feels live during ops.
 */

import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { CONTENT_ASSETS_CHANGED_EVENT } from "@/lib/contentTelemetry";

interface BatchRow {
  id: string;
  created_at: string;
  event_type: string;
  metadata: any;
}

type BatchStatus = "completed" | "generating" | "interrupted" | "failed";

interface BatchSummary {
  batchId: string;
  ts: string;
  status: BatchStatus;
  assetCount: number;
  formats: string[];
  escalations: string[];
  archetypes: string[];
  intensity: string | null;
  durationMs: number | null;
}

const STATUS_STYLE: Record<BatchStatus, string> = {
  completed: "border-cyan/40 text-cyan bg-cyan/10",
  generating: "border-amber-400/40 text-amber-400 bg-amber-400/10 animate-pulse",
  interrupted: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  failed: "border-red-500/50 text-red-400 bg-red-500/10",
};

// Treat a "started" batch with no terminal event after 90s as interrupted.
const STALE_MS = 90_000;

export default function IntelligenceBatchActivity() {
  const [rows, setRows] = useState<BatchRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const { data } = await (supabase as any)
      .from("content_events")
      .select("id, created_at, event_type, metadata")
      .in("event_type", [
        "intelligence_batch_started",
        "intelligence_batch_completed",
        "intelligence_batch_failed",
      ])
      .order("created_at", { ascending: false })
      .limit(120);
    if (data) setRows(data as BatchRow[]);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const handler = () => { void load(); };
    window.addEventListener(CONTENT_ASSETS_CHANGED_EVENT, handler);
    const id = window.setInterval(() => { void load(); }, 15_000);
    return () => {
      window.removeEventListener(CONTENT_ASSETS_CHANGED_EVENT, handler);
      window.clearInterval(id);
    };
  }, [load]);

  const batches = collapseBatches(rows);

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Activity className="w-5 h-5 text-cyan" />
          Intelligence Batch Activity
          <Badge
            variant="outline"
            className="ml-2 text-[10px] uppercase tracking-wider border-glass-border text-muted-foreground font-mono"
          >
            {batches.length} batch{batches.length === 1 ? "" : "es"}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 text-cyan animate-spin" />
          </div>
        ) : batches.length === 0 ? (
          <div className="text-[11px] font-mono text-muted-foreground py-8 text-center">
            No intelligence batches indexed yet. Pipeline idle.
          </div>
        ) : (
          <div className="divide-y divide-glass-border">
            {batches.map((b) => (
              <div
                key={b.batchId}
                className="px-4 py-3 grid grid-cols-12 gap-2 items-center text-[11px] font-mono"
              >
                <span className="col-span-2 text-muted-foreground tabular-nums">
                  {new Date(b.ts).toLocaleString()}
                </span>
                <span className="col-span-1">
                  <span
                    className={`inline-block uppercase tracking-wider px-1.5 py-0.5 rounded-sm border text-[10px] ${STATUS_STYLE[b.status]}`}
                  >
                    {b.status}
                  </span>
                </span>
                <span className="col-span-1 tabular-nums text-foreground">
                  {b.assetCount} ast
                </span>
                <span className="col-span-2 text-muted-foreground uppercase tracking-wider truncate">
                  {b.formats.join(" · ") || "—"}
                </span>
                <span className="col-span-3 text-muted-foreground truncate" title={b.archetypes.join(", ")}>
                  {b.archetypes.length === 0
                    ? "—"
                    : b.archetypes.slice(0, 3).map(formatLabel).join(", ") +
                      (b.archetypes.length > 3 ? ` +${b.archetypes.length - 3}` : "")}
                </span>
                <span className="col-span-1 text-amber-400 uppercase tracking-wider">
                  {b.intensity ?? "—"}
                </span>
                <span className="col-span-2 text-muted-foreground tabular-nums text-right">
                  {b.durationMs != null ? `${b.durationMs}ms` : "in-flight"}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formatLabel(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function collapseBatches(rows: BatchRow[]): BatchSummary[] {
  const map = new Map<string, BatchSummary>();
  for (const r of rows) {
    const meta = r.metadata ?? {};
    const id: string = meta.batch_id ?? r.id;
    const existing = map.get(id);

    const assetCount = Number(meta.asset_count ?? existing?.assetCount ?? 0);
    const formats: string[] = meta.formats ?? existing?.formats ?? [];
    const escalations: string[] = meta.escalation_paths ?? existing?.escalations ?? [];
    const archetypeUsage = meta.archetypes_used ?? null;
    const archetypes: string[] = archetypeUsage
      ? Object.keys(archetypeUsage)
      : (meta.archetypes ?? existing?.archetypes ?? []);
    const intensity: string | null = meta.intensity ?? existing?.intensity ?? null;
    const durationMs: number | null = meta.duration_ms ?? existing?.durationMs ?? null;

    let status: BatchStatus = existing?.status ?? "generating";
    if (r.event_type === "intelligence_batch_completed") status = "completed";
    else if (r.event_type === "intelligence_batch_failed") status = "failed";
    else if (r.event_type === "intelligence_batch_started" && !existing) status = "generating";

    // The "newest" event wins for status because rows are sorted desc.
    if (!existing) {
      map.set(id, {
        batchId: id,
        ts: r.created_at,
        status,
        assetCount,
        formats,
        escalations,
        archetypes,
        intensity,
        durationMs,
      });
    } else {
      // Merge data from older "started" events into the terminal record.
      existing.assetCount = existing.assetCount || assetCount;
      existing.formats = existing.formats.length ? existing.formats : formats;
      existing.escalations = existing.escalations.length ? existing.escalations : escalations;
      existing.archetypes = existing.archetypes.length ? existing.archetypes : archetypes;
      existing.intensity = existing.intensity ?? intensity;
      existing.durationMs = existing.durationMs ?? durationMs;
    }
  }

  // Stale detection: in-flight batches older than STALE_MS → interrupted.
  const now = Date.now();
  for (const b of map.values()) {
    if (b.status === "generating" && now - new Date(b.ts).getTime() > STALE_MS) {
      b.status = "interrupted";
    }
  }

  return Array.from(map.values()).sort(
    (a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime()
  );
}
